import { Button, Grid, List, Row, Skeleton } from "antd";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { CardTitle, ColumnComponent } from "../../components/ui";
import DataContext from "../../context/DataContext";
import {
  SolutionOutlined,
  FileSearchOutlined,
  ToolOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import AuthContext from "../../context/AuthContext";
import QuickbooksContext from "../../context/QuickbooksContext";
import {
  exportDetailFile,
  exportLogs,
  exportReport,
  exportSummaryFile,
} from "../../utils/func/ExportFile";
import { ModalFilterReport } from "../../components/report";
import { useParams, useNavigate } from "react-router-dom";
import {
  FilterProps,
  InvoiceProps,
  UserActionProps,
} from "../../interfaces/interfaces";
import { FILTERS_INITIAL_STATUS } from "../../utils/data";

interface DataType {
  name: string;
  description: string;
  icon: ReactNode;
  endpoint: "summary" | "detail";
  label: string;
  action: () => void | null;
}

export const ReportClient = () => {
  const { idClient } = useParams();
  const navigate = useNavigate();
  const { dataLoading } = useContext(DataContext);
  const { clientInfo } = useContext(AuthContext);
  const { getData } = useContext(QuickbooksContext);
  const { getData: getListData, getReportData } = useContext(DataContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { useBreakpoint } = Grid;
  const { xs } = useBreakpoint();

  useEffect(() => {
    if (idClient) {
      getListData("status", idClient);
      getListData("customer", idClient);
    }
  }, [idClient]);

  const list: DataType[] = [
    {
      name: "Customer Year Summary",
      description:
        "The Customer Year Summary provides a comprehensive breakdown of transactions grouped by customer and year, showing invoice counts, credit memo counts, payment counts, and their respective totals. This report helps analyze customer transaction patterns over time.",
      icon: <SolutionOutlined />,
      endpoint: "summary",
      label: "View Report",
      action: () => navigate(`/client/${idClient}/customer-year-summary`),
    },
    {
      name: "QBD vs QBO Comparison",
      description:
        "Compare QuickBooks Desktop data with QuickBooks Online data by Customer × Year. Upload a QBD SQLite file to see side-by-side comparison of invoices, payments, and credits between your desktop and online systems.",
      icon: <FileSearchOutlined />,
      endpoint: "summary",
      label: "View Report",
      action: () => navigate(`/client/${idClient}/qbd-vs-qbo`),
    },
    {
      name: "A/R Aging Summary (Invoices Only)",
      description:
        "This report provides an accounts receivable aging summary that includes only invoices, excluding credits, payments, deposits, and journal entries. It helps you focus on outstanding invoice balances by aging category.",
      icon: <SolutionOutlined />,
      endpoint: "summary",
      label: "View Report",
      action: () => navigate(`/custom-summary/${idClient}`),
    },
    {
      name: "A/R Aging Summary (Invoices > 1 Day Past Due)",
      description:
        "This report displays an accounts receivable aging summary for invoices that are more than 1 day overdue, excluding  deposits, and journal entries. Use this to identify long overdue receivables.",
      icon: <SolutionOutlined />,
      endpoint: "summary",
      label: "View Report",
      action: () => navigate(`/custom-summary-over/${idClient}`),
    },
    {
      name: "Aging Detail (Invoices Only)",
      description:
        "The Aging Detail report lists outstanding invoices by aging category, excluding credits, payments, deposits, and journal entries. This detailed view helps analyze unpaid invoices without adjustments.",
      icon: <FileSearchOutlined />,
      endpoint: "detail",
      label: "View Report",
      action: () => navigate(`/custom-detail/${idClient}`),
    },
    {
      name: "Invoices and Received Payments Report",
      description:
        " The Invoices and Received Payments report provides a detailed overview of all invoices along with their associated payments. This report helps track payment history and outstanding balances for each invoice.",
      icon: <FileSearchOutlined />,
      endpoint: "detail",
      label: "View Report",
      action: () => navigate(`/invoices-received/${idClient}`),
    },
    {
      name: "Custom Report",
      description:
        "The custom report provides a flexible and customizable way to analyze data based on specific criteria such as Status, Customer, and Date Range. It allows users to generate detailed insights into various aspects of operations, customer interactions, and performance within a specified time frame.",
      icon: <SolutionOutlined />,
      endpoint: "summary",
      label: "Filter",
      action: () => setIsModalOpen(true),
    },
    {
      name: "Summary Report",
      description:
        "The Summary report provides a comprehensive overview of outstanding invoices owed to your organization by clients. This report is designed to facilitate financial analysis and help manage accounts receivable effectively.",
      icon: <ToolOutlined />,
      endpoint: "summary",
      label: "Export",
      action: () =>
        clientInfo &&
        getData({
          endpoint: "summary",
          idClient: clientInfo?._id,
          next: (data) => exportSummaryFile(data),
        }),
    },
    {
      name: "Detail Report",
      description:
        "The Detail Report provides a comprehensive breakdown of outstanding invoices classified by aging categories, enabling detailed analysis of accounts receivable and aging trends.",
      icon: <FileSearchOutlined />,
      endpoint: "detail",
      label: "Export",
      action: () =>
        clientInfo &&
        getData({
          endpoint: "detail",
          idClient: clientInfo?._id,
          next: (data) =>
            exportDetailFile(data, {
              selectedCurrent: [],
              selected_1_30: [],
              selected_31_60: [],
              selected_61_90: [],
              selected_90: [],
            }),
        }),
    },
    {
      name: "Audit Log",
      description:
        "The User Activity Report provides an overview of user interactions and actions performed within the platform over a specified time period.",
      icon: <HistoryOutlined />,
      endpoint: "detail",
      label: "Export",
      action: () =>
        clientInfo &&
        getReportData(
          "logs",
          clientInfo._id,
          FILTERS_INITIAL_STATUS,
          (data: UserActionProps[]) => exportLogs(data),
        ),
    },
  ];
  return (
    <div className="general-view">
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <CardTitle title={clientInfo?.name + " Reports"} />
        </ColumnComponent>
        <ColumnComponent lg={24} xl={24}>
          <List
            loading={dataLoading}
            itemLayout="horizontal"
            dataSource={list}
            bordered
            renderItem={(item) => (
              <List.Item
                actions={[
                  <Button type="link" onClick={item.action}>
                    {item.label}
                  </Button>,
                ]}
              >
                <Skeleton avatar title={false} loading={dataLoading} active>
                  <List.Item.Meta
                    avatar={!xs && item.icon}
                    title={item.name}
                    description={!xs && item.description}
                  />
                </Skeleton>
              </List.Item>
            )}
          />
        </ColumnComponent>
      </Row>
      <ModalFilterReport
        open={isModalOpen}
        onSubmit={(filters: FilterProps) =>
          idClient &&
          getReportData("report", idClient, filters, (data: InvoiceProps[]) =>
            exportReport(data),
          )
        }
        onCancel={() => setIsModalOpen(false)}
      />
    </div>
  );
};
